import { Component, OnInit } from '@angular/core';
import { EmailTemplate, GreetingService } from 'src/app/service/greeting.service';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-email-panel',
  templateUrl: './email-panel.component.html',
  styleUrls: ['./email-panel.component.scss'],
  standalone: false
})
export class EmailPanelComponent implements OnInit {
  templates: EmailTemplate[] = [];
  filteredTemplates: EmailTemplate[] = [];
  isLoading = false;
  errorMessage = '';
  successMessage = '';

  showForm = false;
  isEditMode = false;
  editingId: number | null = null;

  searchTerm = '';
  filterByDate = this.getTodayDate();

  formData: EmailTemplate = this.initializeForm();
  selectedTemplateFile: File | null = null;

  // Dialog properties
  showDeleteDialog = false;
  showViewDialog = false;
  templateToDelete: EmailTemplate | null = null;
  templateToView: EmailTemplate | null = null;

  constructor(private greetingService: GreetingService) {}

  ngOnInit(): void {
    this.refreshTemplates();
  }

  initializeForm(): EmailTemplate {
    return {
      name: '',
      emailSubject: '',
      emailText: '{}',
      emailTemplateName: '',
      itsDayDate: this.getTodayDate(),
      scheduleDate: this.getTodayDate()
    };
  }

  loadTemplates(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.greetingService.getAllTemplates().subscribe({
      next: (data) => {
        this.templates = data;
        this.filterTemplates();
        this.isLoading = false;
      },
      error: (error) => {
        this.errorMessage = 'Failed to load templates';
        this.isLoading = false;
      }
    });
  }

  loadTemplatesByDate(date: string): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.greetingService.getTemplatesByDate(date).subscribe({
      next: (data) => {
        this.templates = data;
        this.filterTemplates();
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'Failed to load templates for selected date';
        this.templates = [];
        this.filteredTemplates = [];
        this.isLoading = false;
      }
    });
  }

  openAddForm(): void {
    this.showForm = true;
    this.isEditMode = false;
    this.editingId = null;
    this.formData = this.initializeForm();
    this.clearSelectedTemplateFile();
  }

  openEditForm(template: EmailTemplate): void {
    this.showForm = true;
    this.isEditMode = true;
    this.editingId = template.id || null;
    this.formData = {
      ...this.initializeForm(),
      ...template,
      emailText: this.formatEmailContextForForm(template.emailText),
      scheduleDate: this.getDateOnly(template.itsDayDate) || this.getTomorrowDate()
    };
    this.clearSelectedTemplateFile();
  }

  closeForm(): void {
    this.showForm = false;
    this.formData = this.initializeForm();
    this.clearSelectedTemplateFile();
  }

  saveTemplate(): void {
    this.setImmediateDate();

    if (!this.validateForm()) {
      return;
    }

    this.isLoading = true;
    const action = this.isEditMode
      ? this.greetingService.updateTemplate(this.editingId!, this.formData)
      : this.greetingService.createTemplate(this.formData);

    this.saveTemplateWithOptionalFile(action).subscribe({
      next: (response) => {
        this.successMessage = `Template ${this.isEditMode ? 'updated' : 'created'} successfully`;
        this.refreshTemplates();
        this.closeForm();
        setTimeout(() => (this.successMessage = ''), 3000);
      },
      error: (error) => {
        this.errorMessage = `Failed to ${this.isEditMode ? 'update' : 'create'} template`;
        this.isLoading = false;
      }
    });
  }

  onTemplateFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;

    this.clearSelectedTemplateFile();
    this.selectedTemplateFile = file;
    if (file) {
      this.formData.emailTemplateName = file.name;
    }
  }

  removeTemplateFile(): void {
    this.clearSelectedTemplateFile();
  }

  onExistingTemplateFileSelected(event: Event, template: EmailTemplate): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;

    if (!file || !template.id) {
      input.value = '';
      return;
    }

    this.isLoading = true;
    this.greetingService.uploadNotificationFile(template.id, file).pipe(
      switchMap(() => this.greetingService.updateTemplate(template.id!, {
        ...template,
        emailTemplateName: file.name
      }))
    ).subscribe({
      next: () => {
        this.successMessage = 'Template file uploaded successfully';
        this.refreshTemplates();
        setTimeout(() => (this.successMessage = ''), 3000);
      },
      error: () => {
        this.errorMessage = 'Failed to upload template file';
        this.isLoading = false;
      },
      complete: () => {
        input.value = '';
      }
    });
  }

  deleteTemplate(template: EmailTemplate): void {
    this.templateToDelete = template;
    this.showDeleteDialog = true;
  }

  confirmDelete(): void {
    if (!this.templateToDelete) return;

    this.isLoading = true;
    this.showDeleteDialog = false;

    this.greetingService.deleteTemplateById(this.templateToDelete.id!).subscribe({
      next: () => {
        this.successMessage = 'Template deleted successfully';
        this.refreshTemplates();
        setTimeout(() => (this.successMessage = ''), 3000);
      },
      error: (error) => {
        this.errorMessage = 'Failed to delete template';
        this.isLoading = false;
      }
    });
  }

  cancelDelete(): void {
    this.showDeleteDialog = false;
    this.templateToDelete = null;
  }

  viewTemplate(template: EmailTemplate): void {
    this.templateToView = template;
    this.showViewDialog = true;
  }

  closeViewDialog(): void {
    this.showViewDialog = false;
    this.templateToView = null;
  }

  filterTemplates(): void {
    this.filteredTemplates = this.templates.filter((template) => {
      let emailTextStr = '';
      if (typeof template.emailText === 'string') {
        emailTextStr = template.emailText;
      } else if (template.emailText && typeof template.emailText === 'object') {
        emailTextStr = JSON.stringify(template.emailText);
      }
      
      const matchesSearch =
        !this.searchTerm ||
        template.name?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        template.emailSubject?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        template.emailTemplateName?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        emailTextStr.toLowerCase().includes(this.searchTerm.toLowerCase());

      const matchesDate = !this.filterByDate || this.getDateOnly(template.itsDayDate) === this.filterByDate;

      return matchesSearch && matchesDate;
    });
  }

  onSearchChange(): void {
    this.filterTemplates();
  }

  onDateFilterChange(): void {
    if (this.filterByDate) {
      this.loadTemplatesByDate(this.filterByDate);
      return;
    }

    this.loadTemplates();
  }

  clearDateFilter(): void {
    if (!this.filterByDate) {
      return;
    }

    this.filterByDate = '';
    this.loadTemplates();
  }

  refreshTemplates(): void {
    if (this.filterByDate) {
      this.loadTemplatesByDate(this.filterByDate);
      return;
    }

    this.loadTemplates();
  }


  validateForm(): boolean {
    if (!this.formData.name?.trim()) {
      this.errorMessage = 'Please enter a template name';
      return false;
    }

    if (!this.formData.emailSubject?.trim()) {
      this.errorMessage = 'Please enter an email subject';
      return false;
    }

    if (!this.formData.emailTemplateName?.trim()) {
      this.errorMessage = 'Please select a template file';
      return false;
    }

    let emailText = this.formData.emailText;
    if (typeof emailText === 'object') {
      emailText = JSON.stringify(emailText);
    }

    const trimmedEmailText = emailText?.trim();
    if (!trimmedEmailText) {
      this.errorMessage = 'Email context must be provided';
      return false;
    }

    try {
      JSON.parse(trimmedEmailText);
    } catch (error) {
      this.errorMessage = 'Email context must be valid JSON';
      return false;
    }

    if (trimmedEmailText === '{}' || trimmedEmailText === '{ }') {
      this.errorMessage = 'Email context cannot be an empty JSON object';
      return false;
    }

    this.formData.emailText = trimmedEmailText;

    this.errorMessage = '';
    return true;
  }

  private saveTemplateWithOptionalFile(action: Observable<EmailTemplate>): Observable<EmailTemplate | unknown> {
    if (!this.selectedTemplateFile) {
      return action;
    }

    return action.pipe(
      switchMap((template) => {
        const notificationId = template.id || this.editingId;

        if (!notificationId) {
          throw new Error('Template saved, but upload failed because the notification id was not returned.');
        }

        return this.greetingService.uploadNotificationFile(notificationId, this.selectedTemplateFile!);
      })
    );
  }

  getTodayDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = `${today.getMonth() + 1}`.padStart(2, '0');
    const day = `${today.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getTomorrowDate(): string {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const year = tomorrow.getFullYear();
    const month = `${tomorrow.getMonth() + 1}`.padStart(2, '0');
    const day = `${tomorrow.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  setImmediateDate(): void {
    this.formData.scheduleDate = this.getTodayDate();
    this.formData.itsDayDate = this.getTodayDate();
  }

  getTemplateDownloadUrl(template: EmailTemplate): string {
    return this.getTemplateFileDownloadUrl(template.emailTemplateName);
  }

  getTemplateFileDownloadUrl(templateName: string): string {
    return this.greetingService.resolveEmailTemplateUrl(templateName);
  }

  getTemplateContext(template: EmailTemplate): string {
    return this.formatEmailContextForDisplay(template.emailText);
  }

  private getDateOnly(dateValue?: string): string {
    if (!dateValue) {
      return '';
    }

    if (/^\d{4}-\d{2}-\d{2}/.test(dateValue)) {
      return dateValue.slice(0, 10);
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return '';
    }

    const year = parsedDate.getFullYear();
    const month = `${parsedDate.getMonth() + 1}`.padStart(2, '0');
    const day = `${parsedDate.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private clearSelectedTemplateFile(): void {
    this.selectedTemplateFile = null;
  }

  private formatEmailContextForForm(emailText: EmailTemplate['emailText']): string {
    if (typeof emailText === 'string') {
      const trimmed = emailText.trim();
      if (!trimmed) {
        return '{}';
      }

      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch {
        return emailText;
      }
    }

    if (emailText && typeof emailText === 'object') {
      return JSON.stringify(emailText, null, 2);
    }

    return '{}';
  }

  private formatEmailContextForDisplay(emailText: EmailTemplate['emailText']): string {
    if (typeof emailText === 'string') {
      const trimmed = emailText.trim();
      return trimmed || 'No email context provided';
    }

    if (emailText && typeof emailText === 'object' && Object.keys(emailText).length) {
      return JSON.stringify(emailText, null, 2);
    }

    return 'No email context provided';
  }
}
